import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import '../css/PublicLanding.css';
import BrandLogo from './BrandLogo';

const SECTION_LINKS = [
  { id: 'anteprima', href: '/#anteprima', label: 'Anteprima' },
  { id: 'servizi', href: '/#servizi', label: 'Funzionalità' },
  { id: 'licenza-card', href: '/#licenza-card', label: 'Richiedi licenza' }
];

const LANGUAGES = ['IT', 'EN'];

/**
 * Navbar condivisa delle pagine pubbliche (PublicLanding, PricingPage, …).
 *
 * Props:
 *  - activeLink {string}  — 'anteprima' | 'servizi' | 'licenza-card' | 'pricing'
 *                           evidenzia la voce attiva.
 */
export default function PublicNavbar({ activeLink = '' }) {
  const [selectedLanguage, setSelectedLanguage] = useState('IT');
  const [isLanguageOpen, setIsLanguageOpen] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const languageMenuRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    const onDocumentClick = (event) => {
      if (languageMenuRef.current && !languageMenuRef.current.contains(event.target)) {
        setIsLanguageOpen(false);
      }
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        setIsLanguageOpen(false);
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', onDocumentClick);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onDocumentClick);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, []);

  // Chiude il menu mobile quando cambia pagina o sezione.
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname, location.hash]);

  const linkClass = (id) => `public-nav-link${activeLink === id ? ' public-nav-link-active' : ''}`;
  const closeMenu = () => setIsMenuOpen(false);

  return (
    <header className={`public-landing-nav${isMenuOpen ? ' is-menu-open' : ''}`}>
      <div className="public-nav-inner">
        <Link to="/" className="public-brand" onClick={closeMenu}>
          <BrandLogo size={30} />
          Neurotraining
        </Link>

        <button
          type="button"
          className="public-nav-toggle"
          aria-label={isMenuOpen ? 'Chiudi menu' : 'Apri menu'}
          aria-expanded={isMenuOpen}
          aria-controls="public-nav-panel"
          onClick={() => setIsMenuOpen((open) => !open)}
        >
          <span className="public-nav-toggle-bar" aria-hidden="true" />
          <span className="public-nav-toggle-bar" aria-hidden="true" />
          <span className="public-nav-toggle-bar" aria-hidden="true" />
        </button>

        <div className="public-nav-panel" id="public-nav-panel">
          <nav className="public-nav-links" aria-label="Navigazione sezione pubblica">
            {SECTION_LINKS.map(({ id, href, label }) => (
              <a
                key={id}
                href={href}
                className={linkClass(id)}
                aria-current={activeLink === id ? 'true' : undefined}
                onClick={closeMenu}
              >
                {label}
              </a>
            ))}
            <Link
              to="/pricing"
              className={linkClass('pricing')}
              aria-current={activeLink === 'pricing' ? 'page' : undefined}
              onClick={closeMenu}
            >
              Piani
            </Link>
          </nav>

          <div className="public-nav-actions">
            <div className="public-language-switch" ref={languageMenuRef}>
              <button
                type="button"
                className="public-language-trigger"
                aria-haspopup="menu"
                aria-expanded={isLanguageOpen}
                aria-label="Seleziona lingua"
                onClick={() => setIsLanguageOpen((open) => !open)}
              >
                <svg viewBox="0 0 24 24" aria-hidden="true" className="public-language-icon">
                  <path
                    d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2zm7.8 9h-3a15.6 15.6 0 0 0-1.3-5A8.1 8.1 0 0 1 19.8 11zM12 4.2A13.4 13.4 0 0 1 13.9 11h-3.8A13.4 13.4 0 0 1 12 4.2zM8.5 6A15.6 15.6 0 0 0 7.2 11h-3A8.1 8.1 0 0 1 8.5 6zM4.2 13h3a15.6 15.6 0 0 0 1.3 5A8.1 8.1 0 0 1 4.2 13zM12 19.8A13.4 13.4 0 0 1 10.1 13h3.8A13.4 13.4 0 0 1 12 19.8zm3.5-1.8a15.6 15.6 0 0 0 1.3-5h3A8.1 8.1 0 0 1 15.5 18z"
                    fill="currentColor"
                  />
                </svg>
                <span>{selectedLanguage}</span>
                <span className="public-language-caret" aria-hidden="true">▾</span>
              </button>

              {isLanguageOpen && (
                <ul className="public-language-menu" role="menu" aria-label="Selezione lingua">
                  {LANGUAGES.map((language) => (
                    <li role="none" key={language}>
                      <button
                        type="button"
                        role="menuitem"
                        className={`public-language-option${selectedLanguage === language ? ' is-active' : ''}`}
                        onClick={() => { setSelectedLanguage(language); setIsLanguageOpen(false); }}
                      >
                        {language}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <Link to="/login" className="public-nav-link public-nav-login" onClick={closeMenu}>Accedi</Link>
            <Link to="/login?demo=1" className="public-nav-cta" onClick={closeMenu}>Prova demo</Link>
          </div>
        </div>
      </div>
    </header>
  );
}
